import * as React from "react";
import "./styles.scss";
import { Dropdown, DropdownItemProps, DropdownProps, Label, LabelProps, Visibility } from "semantic-ui-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import axios, { CancelToken } from "axios";
import { Page } from "@/services/bedrock";

export interface PaginatedDropdownItemProps extends DropdownItemProps {
  searchValues?: string[];
}

interface PaginatedDropdownProps extends DropdownProps {
  loadData: (
    page: number,
    searchQuery?: string,
    cancelToken?: CancelToken
  ) => Promise<Page<PaginatedDropdownItemProps>>;
  initialOptions?: PaginatedDropdownItemProps[];
}

const PaginatedDropdown: React.FC<PaginatedDropdownProps> = ({ loadData, initialOptions, ...props }) => {
  const initialValue = props.value === undefined ? (props.multiple ? [] : undefined) : props.value;
  const [value, setValue] = useState<boolean | number | string | (boolean | number | string)[] | undefined>(
    initialValue
  );
  const [page, setPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [options, setOptions] = useState<{ [key: string]: PaginatedDropdownItemProps }>(() => {
    const records: { [key: string]: PaginatedDropdownItemProps } = {};
    if (initialOptions) {
      initialOptions.forEach(item => {
        records[`key-${item.key}`] = item;
      });
    }
    return records;
  });
  const cancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const debouncedSearch = useMemo(() => debounce((search: string) => fetchMore(1, search), 500), []);

  useEffect(() => {
    if (initialOptions && initialOptions.length > 0) {
      setOptions(prev => {
        const newRecords = { ...prev };
        initialOptions.forEach(item => {
          newRecords[`key-${item.key}`] = item;
        });
        return newRecords;
      });
    }
  }, [initialOptions]);

  useEffect(() => {
    const newValue = props.value === undefined ? (props.multiple ? [] : undefined) : props.value;
    setValue(newValue);
  }, [props.value]);

  const fetchMore = useCallback(
    (page: number, search?: string) => {
      if (cancelTokenRef.current && search === undefined) return;

      cancelTokenRef.current?.cancel("Cancelled due to new request");
      const cancelTokenSource = axios.CancelToken.source();
      cancelTokenRef.current = cancelTokenSource;
      if (search === undefined) search = searchQuery;
      else setSearchQuery(search);

      loadData(page, search, cancelTokenSource.token)
        .then(res => {
          setHasMorePages(res.pagination.totalPages > res.pagination.page);
          setPage(res.pagination.page);
          setOptions(prev => {
            const newRecords = { ...prev };
            res.content.forEach((item: PaginatedDropdownItemProps) => {
              newRecords[`key-${item.key}`] = item;
            });
            return newRecords;
          });
        })
        .catch(console.error)
        .finally(() => {
          cancelTokenRef.current = null;
        });
    },
    [loadData, searchQuery]
  );

  const effectiveOptions = useMemo(() => {
    var effectiveOptions = [...Object.values(options)];
    if (hasMorePages || cancelTokenRef.current) {
      effectiveOptions.push({
        key: "infinite-loader",
        disabled: true,
        content: (
          <Visibility onOnScreen={() => fetchMore(page + 1)}>
            <span className="infinite-loader">Loading...</span>
          </Visibility>
        ),
      });
    }
    return effectiveOptions;
  }, [page, options, hasMorePages, cancelTokenRef.current]);

  useEffect(() => fetchMore(page + 1), []);

  const handleSearchChange = useCallback((_: any, data: any) => {
    debouncedSearch(data.searchQuery);
    setHasMorePages(true);
  }, []);

  const handleOnChange = (event: any, data: any) => {
    setValue(data.value);
    props.onChange?.(event, data);
  };

  const handleSearch = (options: PaginatedDropdownItemProps[], value: string) => {
    return options.filter((o: PaginatedDropdownItemProps) => {
      if (o.key === "infinite-loader") return true;
      if (o.searchValues) return o.searchValues.some(v => v.toLowerCase().includes(value.toLowerCase()));
      return `${o.text}`.toLowerCase().includes(value.toLowerCase());
    });
  };

  return (
    <Dropdown
      className="paginatedDropdown"
      {...props}
      value={value}
      fluid
      selection
      options={effectiveOptions}
      onSearchChange={handleSearchChange}
      search={handleSearch}
      onChange={handleOnChange}
    />
  );
};
export default PaginatedDropdown;
